/**
 * Autoplay fallback for muted background videos (hero, editorial chapters, details).
 *
 * Some browsers refuse autoplay even for muted, inline videos: Samsung Internet in power
 * saving mode, Android battery/data saver, iOS Low Power Mode, Firefox/Brave with autoplay
 * blocked. When play() is refused:
 *   1. The video keeps its poster with a plain play triangle in the centre. The start button
 *      covers the whole video, so a tap plays it rather than following a link behind it
 *      (the hero is a link to the product).
 *   2. The first tap, click or key press anywhere on the page also starts it, since browsers
 *      accept any of those as permission to play.
 *   3. Once it has played, the centre triangle goes and a small play/pause square appears
 *      bottom right. Videos that autoplay normally get neither.
 */

const SELECTOR = 'video[autoplay][muted]:not([controls])';
const GESTURES = ['pointerup', 'touchend', 'click', 'keydown', 'scrollend'];

const PLAY_ICON =
  '<svg class="rm-video-toggle__play" aria-hidden="true" viewBox="0 0 12 12" width="12" height="12"><path d="M3 1.5v9l7.5-4.5z" fill="currentColor"/></svg>';
const PAUSE_ICON =
  '<svg class="rm-video-toggle__pause" aria-hidden="true" viewBox="0 0 12 12" width="12" height="12"><path d="M3 1.5h2v9H3zM7 1.5h2v9H7z" fill="currentColor"/></svg>';
const START_ICON =
  '<svg aria-hidden="true" viewBox="0 0 24 28" width="24" height="28"><path d="M0 0v28l24-14z" fill="currentColor"/></svg>';

/** `?rm_autoplay_blocked` in the URL acts out a refused autoplay once per video, for testing. */
const simulate = new URLSearchParams(location.search).has('rm_autoplay_blocked');
const simulated = new WeakSet();

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/** Videos whose autoplay was refused and that have not played yet. */
const blocked = new Set();
const seen = new WeakSet();

/** @param {HTMLVideoElement} video */
function play(video) {
  if (simulate && !simulated.has(video)) {
    simulated.add(video);
    video.pause();
    blocked.add(video);
    addStart(video);
    return Promise.resolve();
  }
  return video
    .play()
    .then(() => blocked.delete(video))
    .catch((error) => {
      if (error?.name !== 'NotAllowedError' || blocked.has(video)) return;
      blocked.add(video);
      addStart(video);
    });
}

/** @param {HTMLElement} host */
function position(host) {
  if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
}

/** @param {HTMLVideoElement} video */
function addStart(video) {
  const host = video.parentElement;
  if (!host) return;
  position(host);

  // Browsers draw their own round play button inside a blocked video, and it cannot be
  // styled away everywhere (Samsung Internet), so the video is hidden behind its poster.
  if (video.poster) {
    host.style.setProperty('--rm-video-poster', `url("${video.poster}")`);
    host.classList.add('rm-video-host--blocked');
  }
  visible.observe(video);

  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'rm-video-start';
  button.setAttribute('aria-label', 'Play video');
  button.innerHTML = START_ICON;
  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    play(video);
  });
  host.append(button);

  video.addEventListener(
    'playing',
    () => {
      button.remove();
      host.classList.remove('rm-video-host--blocked');
      visible.unobserve(video);
      addToggle(video, host);
    },
    { once: true }
  );
}

/**
 * @param {HTMLVideoElement} video
 * @param {HTMLElement} host
 */
function addToggle(video, host) {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'rm-video-toggle';
  button.innerHTML = PLAY_ICON + PAUSE_ICON;

  const sync = () => {
    button.dataset.state = video.paused ? 'paused' : 'playing';
    button.setAttribute('aria-label', video.paused ? 'Play video' : 'Pause video');
  };

  button.addEventListener('click', (event) => {
    event.preventDefault();
    event.stopPropagation();
    if (video.paused) video.play().catch(() => {});
    else video.pause();
  });
  video.addEventListener('play', sync);
  video.addEventListener('pause', sync);
  sync();

  host.append(button);
}

/** Try again whenever a blocked video scrolls into view (some browsers relent after a touch). */
const visible = new IntersectionObserver((entries) => {
  for (const entry of entries) {
    const video = /** @type {HTMLVideoElement} */ (entry.target);
    if (entry.isIntersecting && blocked.has(video) && !reducedMotion.matches && !simulate) video.play().catch(() => {});
  }
});

/** @param {Event} event */
function onGesture(event) {
  if (!blocked.size || reducedMotion.matches) return;
  // A scroll never unblocks a real browser on its own; the test switch mimics that.
  if (simulate && event.type === 'scrollend') return;
  if (event.target instanceof Element && event.target.closest('.rm-video-start, .rm-video-toggle')) return;
  for (const video of blocked) play(video);
}

/** @param {HTMLVideoElement} video */
function check(video) {
  if (seen.has(video)) return;
  seen.add(video);
  if (video.paused || simulate) play(video);
}

/** @param {ParentNode} root */
function scan(root) {
  if (root instanceof HTMLVideoElement && root.matches(SELECTOR)) check(root);
  if ('querySelectorAll' in root) root.querySelectorAll(SELECTOR).forEach((video) => check(/** @type {HTMLVideoElement} */ (video)));
}

for (const type of GESTURES) document.addEventListener(type, onGesture, { capture: true, passive: true });

scan(document);

new MutationObserver((records) => {
  for (const record of records) {
    record.addedNodes.forEach((node) => {
      if (node instanceof Element) scan(node);
    });
  }
}).observe(document.body, { childList: true, subtree: true });
